/**
 * Component Editor Page
 *
 * Paper "Experiments · 02 Editor": the page under test in an iframe, a top bar
 * (name, version switcher, device, Launch test) and a right panel with Element,
 * Changes and Launch tabs. Element tests are built and launched from here.
 */

import { useOutletContext, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/contexts/AuthContext';
import { useWorkspaceSafe } from '@/lib/contexts/WorkspaceContext';
import { EditorShell } from '@/components/experiments/ComponentEditor/EditorShell';
import { toAbsolutePageUrl } from '@/components/experiments/ComponentEditor/iframeUrl';
import { readSplitTestDraft } from '@/components/experiments/splitTestDraft';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowLeft01Icon, InformationCircleIcon } from "@hugeicons/core-free-icons"

type EditorOutletContext = {
  propertyId?: string
  currentDomain?: string
  property?: { domain?: string; dataSources?: Array<{ config?: { domain?: string } }> }
}

export default function ComponentEditor() {
  const { draftId, workspaceId, propertySlugId } = useParams<{ draftId: string; workspaceId?: string; propertySlugId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const ctx = useOutletContext<EditorOutletContext | undefined>();
  const workspace = useWorkspaceSafe();

  // The draft knows which site it tests; prefer it over the layout's selection.
  const draftPropertyId = draftId ? readSplitTestDraft(draftId)?.propertyId : undefined;
  const propertyId = draftPropertyId || ctx?.propertyId || workspace?.selectedPropertyId || '';
  // Real domains first: the layout's currentDomain falls back to the workspace
  // or property name, which would load https://<name>/ and never connect.
  const rawPageUrl = searchParams.get('pageUrl');
  const pageUrl = rawPageUrl
    ? toAbsolutePageUrl(rawPageUrl, [
        workspace?.enabledProperties.find((p) => p.propertyId === propertyId)?.domain,
        ctx?.property?.dataSources?.[0]?.config?.domain,
        ctx?.property?.domain,
        ctx?.currentDomain,
      ])
    : null;
  const initialSelector = searchParams.get('element');
  const basePath = workspaceId ? `/workspace/${workspaceId}` : propertySlugId ? `/p/${propertySlugId}` : '';

  // Handle missing params - show stateful error surface with recovery actions
  if (!draftId || !pageUrl || !token) {
    const missingItems: string[] = [];
    if (!draftId) missingItems.push('a draft');
    if (!pageUrl) missingItems.push('the page to test');
    if (!token) missingItems.push('a signed-in session');

    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-6">
        <Card className="max-w-md w-full">
          <CardHeader className="space-y-1">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-2">
              <HugeiconsIcon icon={InformationCircleIcon} size={24} className="text-muted-foreground" />
            </div>
            <CardTitle className="text-xl">The editor can't open yet</CardTitle>
            <CardDescription>
              It needs {missingItems.join(', ')}. Start the test again from Experiments.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate(`${basePath}/split-testing/new${window.location.search}`)} className="w-full gap-2" variant="outline">
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
              Pick a page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <EditorShell
      pageUrl={pageUrl}
      draftId={draftId}
      authToken={token}
      initialSelector={initialSelector}
      onBack={() => navigate(-1)}
      propertyId={propertyId}
      basePath={basePath}
    />
  );
}
